import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Button, Flexbox } from '@ibinder/common-frontend';
import { useFeatureToggle } from '@ibinder/ui-common-hooks';
import { removeUserAssignments } from 'Shared/assets/settings/featureToggleConsts';
import {
  GET_USER_RESOURCE_ACCESS,
  UserResourceAccessResponse
} from './getUserResourceAccess';
import { groupUserResourceAccess } from './groupUserResourceAccess';
import { REMOVE_USER_FROM_RESOURCES } from './removeUserFromResources';
import {
  AssignmentsTarget,
  RemovableResourceKind,
  ResourceDeletions,
  ResourceRemoval,
  UserAccessGroups,
  deletionCount,
  readResourceDeletions,
  resourceSelectionKey
} from './userResourceAccess.types';
import AllUserAssignments from './AllUserAssignments';
import UserAssignments from './UserAssignments';
import UserResourceAccessTree from './UserResourceAccessTree';
import { useUserAccessBreadcrumb } from '../../../userAccessBreadcrumb';

type Props = {
  userId: string;
  userName: string;
  onBack: () => void;
};

type RemovalNotice = {
  tone: 'success' | 'warning' | 'error';
  text: string;
};

const Container = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled(Flexbox)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const HeaderActions = styled(Flexbox)`
  align-items: center;
  gap: 0.75rem;
`;

const BackButtonContainer = styled(Flexbox)`
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 1rem;
`;

const HeaderButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const RemoveButton = styled(HeaderButton)`
  && {
    padding: 0 1.25rem;
  }
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  margin: 0;
`;

const StatusMessage = styled.p`
  margin: 1rem 0;
`;

const Notice = styled.p<{ $tone: RemovalNotice['tone'] }>`
  color: ${({ $tone, theme }) => {
    if ($tone === 'success') {
      return theme.colors.primary;
    }
    if ($tone === 'error') {
      return '#b91c1c';
    }
    return '#b45309';
  }};
  margin: 0 0 1rem 0;
`;

const collectNames = (groups: UserAccessGroups) => {
  const names = new Map<string, string>();
  [...groups.direct, ...groups.indirect].forEach((organization) => {
    names.set(
      resourceSelectionKey('organization', organization.id),
      organization.name
    );
    organization.binders.forEach((binder) => {
      names.set(resourceSelectionKey('binder', binder.id), binder.name);
    });
    organization.tenders.forEach((tender) => {
      names.set(resourceSelectionKey('tender', tender.id), tender.name);
    });
  });
  return names;
};

const describeDeletions = (deletions: ResourceDeletions) => {
  const parts: string[] = [];
  const add = (count: number, singular: string, plural: string) => {
    if (count > 0) {
      parts.push(`${count} ${count === 1 ? singular : plural}`);
    }
  };
  add(deletions.organizations.length, 'organization', 'organizations');
  add(deletions.binders.length, 'binder', 'binders');
  add(deletions.tenders.length, 'tender', 'tenders');
  if (parts.length <= 1) {
    return parts[0] || '';
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const describeFailures = (
  deletions: ResourceDeletions,
  names: Map<string, string>
) => {
  const items: string[] = [];
  const add = (kind: RemovableResourceKind, label: string, ids: string[]) => {
    ids.forEach((id) => {
      const name = names.get(resourceSelectionKey(kind, id)) || id;
      items.push(`${label} ${name}`);
    });
  };
  add('organization', 'Organization', deletions.organizations);
  add('binder', 'Binder', deletions.binders);
  add('tender', 'Tender', deletions.tenders);
  return items.join(', ');
};

const selectionFromDeletions = (deletions: ResourceDeletions) => {
  const next = new Set<string>();
  deletions.organizations.forEach((id) =>
    next.add(resourceSelectionKey('organization', id))
  );
  deletions.binders.forEach((id) =>
    next.add(resourceSelectionKey('binder', id))
  );
  deletions.tenders.forEach((id) =>
    next.add(resourceSelectionKey('tender', id))
  );
  return next;
};

const idsOfKind = (selected: Set<string>, kind: RemovableResourceKind) =>
  Array.from(selected)
    .filter((key) => key.indexOf(`${kind}:`) === 0)
    .map((key) => key.slice(kind.length + 1));

const collectOrganizationIds = (groups: UserAccessGroups) => {
  const organizationIds = new Map<string, string>();
  [...groups.direct, ...groups.indirect].forEach((organization) => {
    organization.binders.forEach((binder) => {
      organizationIds.set(
        resourceSelectionKey('binder', binder.id),
        organization.id
      );
    });
    organization.tenders.forEach((tender) => {
      organizationIds.set(
        resourceSelectionKey('tender', tender.id),
        organization.id
      );
    });
  });
  return organizationIds;
};

const withOrganizationIds = (
  kind: 'binder' | 'tender',
  ids: string[],
  organizationIds: Map<string, string>
): ResourceRemoval[] =>
  ids.map((id) => ({
    id,
    organizationId: organizationIds.get(resourceSelectionKey(kind, id)) || ''
  }));

const UserResourceAccess = ({ userId, userName, onBack }: Props) => {
  const [
    loadUserResourceAccess,
    { data: resourceAccessData, loading, error, called }
  ] = useLazyQuery(GET_USER_RESOURCE_ACCESS, {
    fetchPolicy: 'no-cache'
  });
  const [removeUserFromResources] = useMutation(REMOVE_USER_FROM_RESOURCES);
  const { isEnabled: canRemoveUserAssignments } = useFeatureToggle(
    removeUserAssignments as Parameters<typeof useFeatureToggle>[0]
  );
  const [assignmentsTarget, setAssignmentsTarget] =
    useState<AssignmentsTarget | null>(null);
  const [showAllAssignments, setShowAllAssignments] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<RemovalNotice | null>(null);
  const [removing, setRemoving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { setUserName, setAssignmentsOpen, actions } =
    useUserAccessBreadcrumb();
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    setUserName(userName);
    actions.current.goToListing = () => onBackRef.current();
    return () => {
      setUserName(null);
      actions.current.goToListing = () => undefined;
    };
  }, [userName, setUserName, actions]);

  useEffect(() => {
    setAssignmentsOpen(Boolean(assignmentsTarget) || showAllAssignments);
    actions.current.goToResources = () => {
      setAssignmentsTarget(null);
      setShowAllAssignments(false);
    };
    return () => {
      setAssignmentsOpen(false);
      actions.current.goToResources = () => undefined;
    };
  }, [assignmentsTarget, showAllAssignments, setAssignmentsOpen, actions]);

  useEffect(() => {
    setAssignmentsTarget(null);
    setShowAllAssignments(false);
    setSelected(new Set());
    setNotice(null);
    setRefreshing(false);
  }, [userId]);

  useEffect(() => {
    loadUserResourceAccess({ variables: { userId } });
  }, [userId]);

  const accessGroups = useMemo(
    () =>
      resourceAccessData?.getUserResourceAccess
        ? groupUserResourceAccess(
            resourceAccessData.getUserResourceAccess as UserResourceAccessResponse
          )
        : { direct: [], indirect: [] },
    [resourceAccessData]
  );
  const organizations = useMemo(
    () =>
      [...accessGroups.direct, ...accessGroups.indirect].map(
        (organization) => ({
          id: organization.id,
          name: organization.name
        })
      ),
    [accessGroups]
  );
  const hasLoadedAccess = Boolean(resourceAccessData?.getUserResourceAccess);
  const isInitialLoading = !hasLoadedAccess && (!called || loading);
  const hasAccess =
    accessGroups.direct.length > 0 || accessGroups.indirect.length > 0;

  const refreshResources = async () => {
    if (refreshing || removing || isInitialLoading) {
      return;
    }
    setRefreshing(true);
    setNotice(null);
    try {
      const result = await loadUserResourceAccess({ variables: { userId } });
      if (result.error) {
        setNotice({
          tone: 'error',
          text: 'Unable to refresh user access.'
        });
      }
    } catch (refreshError) {
      setNotice({
        tone: 'error',
        text: 'Unable to refresh user access.'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const setSelectedResources = (
    resources: { kind: RemovableResourceKind; id: string }[],
    isSelected: boolean
  ) => {
    setNotice(null);
    setSelected((current) => {
      const next = new Set(current);
      resources.forEach((resource) => {
        const key = resourceSelectionKey(resource.kind, resource.id);
        if (isSelected) {
          next.add(key);
        } else {
          next.delete(key);
        }
      });
      return next;
    });
  };

  const removeSelected = async () => {
    if (removing) {
      return;
    }
    const organizationIds = idsOfKind(selected, 'organization');
    const binderIds = idsOfKind(selected, 'binder');
    const tenderIds = idsOfKind(selected, 'tender');
    const summary = describeDeletions({
      organizations: organizationIds,
      binders: binderIds,
      tenders: tenderIds
    });
    if (!summary || !window.confirm(`Remove ${userName} from ${summary}?`)) {
      return;
    }

    const names = collectNames(accessGroups);
    const resourceOrganizationIds = collectOrganizationIds(accessGroups);
    setRemoving(true);
    setNotice(null);
    try {
      const result = await removeUserFromResources({
        variables: {
          userId,
          body: {
            organizations: organizationIds,
            binders: withOrganizationIds(
              'binder',
              binderIds,
              resourceOrganizationIds
            ),
            tenders: withOrganizationIds(
              'tender',
              tenderIds,
              resourceOrganizationIds
            )
          }
        }
      });
      try {
        await loadUserResourceAccess({ variables: { userId } });
      } catch (reloadError) {
        // The deletion result is still reported; the query error covers the list.
        console.debug(reloadError);
      }
      const payload = result.data?.removeUserFromResources;
      const successful = readResourceDeletions(payload?.successfulDeletions);
      const failed = readResourceDeletions(payload?.failedDeletions);
      const removed = describeDeletions(successful);
      if (deletionCount(failed) === 0) {
        setSelected(new Set());
        setNotice({
          tone: 'success',
          text: removed
            ? `Removed from ${removed}.`
            : 'No resources were removed.'
        });
      } else {
        setSelected(selectionFromDeletions(failed));
        const failedText = describeFailures(failed, names);
        setNotice({
          tone: 'warning',
          text: removed
            ? `Could not remove the user from ${failedText}. Removed from ${removed}.`
            : `Could not remove the user from ${failedText}.`
        });
      }
    } catch (removeError) {
      setNotice({
        tone: 'error',
        text: 'Unable to remove the user from the selected resources.'
      });
    } finally {
      setRemoving(false);
    }
  };

  if (showAllAssignments) {
    return (
      <AllUserAssignments
        userId={userId}
        userName={userName}
        organizations={organizations}
        onBack={() => setShowAllAssignments(false)}
      />
    );
  }

  if (assignmentsTarget) {
    return (
      <UserAssignments
        userId={userId}
        userName={userName}
        target={assignmentsTarget}
        onBack={() => setAssignmentsTarget(null)}
      />
    );
  }

  return (
    <Container>
      <Header>
        <Title>{`Access for ${userName}`}</Title>
        <HeaderActions>
          <HeaderButton
            btnStyle="secondary"
            label="List all user assignments"
            disabled={
              refreshing ||
              removing ||
              isInitialLoading ||
              organizations.length === 0
            }
            onClick={() => setShowAllAssignments(true)}
          />
          <HeaderButton
            btnStyle="secondary"
            label="Refresh"
            disabled={refreshing || removing || isInitialLoading}
            loading={refreshing}
            onClick={refreshResources}
          />
          {canRemoveUserAssignments && selected.size > 0 && (
            <RemoveButton
              btnStyle="delete"
              label="Remove user from resources"
              disabled={removing || refreshing}
              loading={removing}
              onClick={removeSelected}
            />
          )}
        </HeaderActions>
      </Header>

      {notice && <Notice $tone={notice.tone}>{notice.text}</Notice>}

      {error && !hasLoadedAccess ? (
        <StatusMessage>Unable to load user access.</StatusMessage>
      ) : !isInitialLoading && !hasAccess ? (
        <StatusMessage>
          This user does not have access to any resources.
        </StatusMessage>
      ) : (
        <UserResourceAccessTree
          userId={userId}
          groups={accessGroups}
          loading={isInitialLoading}
          selected={selected}
          onSetSelected={setSelectedResources}
          onShowAssignments={setAssignmentsTarget}
        />
      )}
      <BackButtonContainer>
        <HeaderButton btnStyle="secondary" label="Back" onClick={onBack} />
      </BackButtonContainer>
    </Container>
  );
};

export default UserResourceAccess;
